'use client';

import { CompanyHeaderWithTabs } from '@/components/fmp/company/company-header-with-tabs';
import { CompanySearch } from '@/components/fmp/company/company-search';
import { CompanyTabs } from '@/components/fmp/company/company-tabs';
import { IncomeStatementView } from '@/components/fmp/financial-graphs/income-statement';
import { FinancialStatementsNavigation } from '@/components/fmp/financial-graphs/financial-statements-navigation';
import { CompanySkeleton } from '@/components/fmp/company/company-skeleton';
import { ErrorView } from '@/components/fmp/company/error-view';
import { NoteSheet } from '@/components/fmp/watchlist/stock-notes/note-sheet';
import { useCompanyPage } from '@/hooks/use-company-page';
import { useCompanyStatements } from '@/lib/stores/financial-statements-store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { queryConfig } from '@/lib/config/query-client-config';
import { useRef } from 'react';

const queryClient = new QueryClient(queryConfig);

interface CompanyIncomeStatementPageClientProps {
  symbol: string;
}

function CompanyIncomeStatementPage({
  symbol,
}: CompanyIncomeStatementPageClientProps) {
  const {
    noteSheetOpen,
    tabs,
    activeTab,
    data,
    isLoading,
    error,
    handleSearchSelect,
    handleTabHover,
    handleTabSelect,
    handleTabRemove,
    handleNotesOpen,
    handleNotesClose,
  } = useCompanyPage(symbol, 'financials-income-statement');

  const { period, years } = useCompanyStatements(symbol);
  const chartRef = useRef<HTMLDivElement>(null);

  if (isLoading) {
    return (
      <div className="mx-auto md:mx-6 py-8 px-4">
        <CompanySkeleton variant="financials" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto md:mx-6 py-8 px-4">
        <ErrorView
          message={
            error instanceof Error
              ? error.message
              : 'Failed to load company data'
          }
        />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="mx-auto md:mx-6 py-8 px-4">
      <div className="space-y-6">
        {/* Header with search */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Income Statement</h1>
          <CompanySearch
            variant="header"
            onSelectCompany={handleSearchSelect}
          />
        </div>

        {/* Tab management */}
        <CompanyTabs
          tabs={tabs}
          activeTab={activeTab}
          currentPage="financials-income-statement"
          onTabSelect={handleTabSelect}
          onTabRemove={handleTabRemove}
          onTabHover={handleTabHover}
          onNotesClick={handleNotesOpen}
        >
          <CompanyHeaderWithTabs
            profile={data.profile}
            quote={data.quote}
            keyMetrics={data.keyMetrics}
          />

          <FinancialStatementsNavigation symbol={activeTab || symbol}>
            <IncomeStatementView
              symbol={activeTab || symbol}
              period={period}
              years={years}
              chartRef={chartRef}
            />
          </FinancialStatementsNavigation>
        </CompanyTabs>

        {activeTab && (
          <NoteSheet
            symbol={activeTab}
            isOpen={noteSheetOpen}
            onClose={handleNotesClose}
            onNavigate={() => {}}
          />
        )}
      </div>
    </div>
  );
}

export function CompanyIncomeStatementPageClient({
  symbol,
}: CompanyIncomeStatementPageClientProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <CompanyIncomeStatementPage symbol={symbol} />
    </QueryClientProvider>
  );
}
